import { Button, Tooltip, type ButtonProps } from '@mui/material'
import {
  createLink,
  useRouter,
  type AnyRoute,
  type LinkComponent,
  type LinkProps,
  type StaticDataRouteOption,
} from '@tanstack/react-router'
import React, { createElement, type ReactNode } from 'react'
import RbacAccessDeniedPage from '~/components/communication/RbacAccessDeniedPage'
import { frenchPluralSuffix } from '~/lib/utils'
import { useAbility, type AppAbility, type PermissionTuple } from '~/utils/ability'
import {
  resolveMissingPermissions,
  type PermissionRequirement,
} from '~/utils/permissionRequirement'
import { describePermission } from '~/utils/permissionLabels'
import type { FileRoutesByTo } from '../routeTree.gen'

export type RouteTo = Extract<keyof FileRoutesByTo, string>

// Le vocabulaire des exigences et leur résolution vivent dans un module pur
// (`utils/permissionRequirement.ts`) : ils sont réexportés ici, d'où tous les appelants les
// importent déjà.
export {
  normalizePermissions,
  resolveMissingPermissions,
  type PermissionRequirement,
  type RoutePermission,
} from '~/utils/permissionRequirement'

export function useRouteStaticData(to: RouteTo): StaticDataRouteOption | undefined {
  const router = useRouter()
  type FileRouteData = FileRoutesByTo[keyof FileRoutesByTo]
  // @ts-expect-error
  const routeData = (router.routesByPath[to] ?? router.routesByPath[`${to}/`]) as FileRouteData
  return routeData.options.staticData
}

/**
 * Permissions déclarées par une route ET par toutes ses descendantes — une entrée par route qui en
 * déclare une.
 *
 * Sert à raisonner sur un ensemble d'écrans (une entrée de menu et ses sous-onglets) sans avoir à
 * redéclarer quoi que ce soit : la seule source reste le `staticData.permission` de chaque route.
 */
export const useDescendantRoutePermissions = (to: string): PermissionRequirement[] =>
  descendantRoutePermissions(useRouter(), to)

/** Version pure, pour les appelants qui doivent traiter plusieurs routes (le menu latéral). */
export const descendantRoutePermissions = (
  router: ReturnType<typeof useRouter>,
  to: string,
): PermissionRequirement[] =>
  Object.entries(router.routesByPath as unknown as Record<string, AnyRoute>)
    .filter(([path]) => path === to || path.startsWith(`${to}/`))
    .map(([, route]) => route.options?.staticData?.permission as PermissionRequirement | undefined)
    .filter((permission): permission is PermissionRequirement => permission !== undefined)

type MUILinkProps = Omit<LinkProps, 'children'> & {
  children: ReactNode
  authorized: boolean
  /** Droits requis manquants — affichés dans le tooltip quand `authorized` est faux. */
  missingPermissions?: PermissionTuple[]
} & Pick<ButtonProps, 'variant' | 'sx' | 'color'>

const PERMISSION_DENIED_MESSAGE = "Vous n'êtes pas autorisé à effectuer cette action"

/** Contenu du tooltip de refus : message + liste des droits exacts à obtenir. */
export function PermissionDeniedTooltip({
  missingPermissions,
}: {
  missingPermissions?: PermissionTuple[]
}) {
  const described = (missingPermissions ?? []).map(describePermission)
  const plural = frenchPluralSuffix(described.length)

  return (
    <div className="text-xs">
      <p>{PERMISSION_DENIED_MESSAGE}</p>
      {described.length > 0 && (
        <>
          <p className="mt-2 font-semibold">
            Droit{plural} manquant{plural} :
          </p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {described.map((permission) => (
              <li key={permission.key}>
                <span className="font-medium">{permission.actionLabel}</span>
                {' — '}
                {permission.segments.join(' › ')}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

type MuiLinkComponentWithHiddenAuthorized = React.ForwardRefExoticComponent<
  Omit<MUILinkProps, 'authorized' | 'missingPermissions'> & React.RefAttributes<HTMLAnchorElement>
>
const MUILinkComponent = React.forwardRef<HTMLAnchorElement, MUILinkProps>((props, ref) => {
  // Props internes : on les retire avant de spread sur le Button (sinon attributs DOM invalides).
  const { authorized, missingPermissions, ...buttonProps } = props
  const isDisabled = buttonProps.disabled || !authorized

  if (!authorized) {
    return (
      <Tooltip title={<PermissionDeniedTooltip missingPermissions={missingPermissions} />}>
        <span>
          <Button ref={ref} component="a" {...buttonProps} disabled />
        </span>
      </Tooltip>
    )
  }
  return (
    <Button ref={ref} component="a" {...buttonProps} disabled={isDisabled}>
      {buttonProps.children as ReactNode}
    </Button>
  )
})

const CreatedLinkComponent = createLink(MUILinkComponent)

/** Version hook, pour les composants guardés : route, lien, bouton de mutation, onglet. */
export const useMissingPermissions = (
  permission?: PermissionRequirement | null,
): PermissionTuple[] => resolveMissingPermissions(useAbility(), permission)

export interface PermissionGuard {
  authorized: boolean
  missingPermissions: PermissionTuple[]
  /** Prêt à poser dans un `Tooltip` ; `undefined` quand l'action est autorisée. */
  tooltip: ReactNode | undefined
}

/** Version prête à l'emploi pour les composants guardés : le tuple v2, et rien d'autre. */
export const usePermissionGuard = (permission?: PermissionRequirement | null): PermissionGuard => {
  const missingPermissions = useMissingPermissions(permission)
  const authorized = missingPermissions.length === 0

  return {
    authorized,
    missingPermissions,
    tooltip: authorized ? undefined : (
      <PermissionDeniedTooltip missingPermissions={missingPermissions} />
    ),
  }
}

/** Droits manquants au regard du `staticData.permission` d'une route CIBLE. */
export const useTargetRoutePermissions = (to: RouteTo): PermissionTuple[] => {
  const staticData = useRouteStaticData(to)
  return useMissingPermissions(staticData?.permission ?? null)
}

/** Disclaimer: only works with absolute urls */
export const AuthorizedButtonLink: LinkComponent<MuiLinkComponentWithHiddenAuthorized> = (
  props,
) => {
  const staticData = useRouteStaticData(props.to as RouteTo)
  const permission = staticData?.permission as PermissionRequirement | undefined
  const missingPermissions = useMissingPermissions(permission ?? null)

  // Une route sans permission v2 déclarée n'est jamais rendue accessible.
  const authorized = permission !== undefined && missingPermissions.length === 0

  const newProps = { ...props, authorized, missingPermissions }

  return <CreatedLinkComponent {...newProps} />
}

export class PermissionRouteError extends Error {
  /** Toutes les permissions manquantes (sémantique ALL) — affichées par la page d'accès refusé. */
  readonly missingPermissions: PermissionTuple[]

  constructor(missingPermissions: PermissionTuple[]) {
    super(`Missing permissions: ${missingPermissions.map((p) => p.join('.')).join(', ')}`)
    this.name = 'PermissionRouteError'
    this.missingPermissions = missingPermissions
  }
}

export function withRoutePermissions<T extends AnyRoute>(route: T): T {
  const opts = route.options
  const _beforeLoad = opts.beforeLoad
  opts.beforeLoad = function beforeLoad(this: any, ctx: any) {
    const permission: PermissionRequirement | undefined = this.staticData?.permission
    if (permission) {
      const ability: AppAbility = ctx.context.ability
      // Sémantique ET par défaut ; `{ any: [...] }` pour une page accessible via l'un OU l'autre de
      // ses sous-onglets. On collecte les tuples manquants pour les afficher à l'utilisateur.
      const missing = resolveMissingPermissions(ability, permission)
      if (missing.length > 0) {
        throw new PermissionRouteError(missing)
      }
    }
    return _beforeLoad?.call(this, ctx)
  }
  const _errorComponent = opts.errorComponent
  opts.errorComponent = function errorComponent(props: any) {
    if (_errorComponent) {
      return createElement(_errorComponent, props)
    }
    if (props.error instanceof PermissionRouteError) {
      return <RbacAccessDeniedPage missingPermissions={props.error.missingPermissions} />
    }
    throw props.error
  }
  return route
}
